/**
 * Drop rolls — pure logic. Called when a monster dies.
 *
 * Rolls independent chances for equipment (with a weighted rarity), food
 * (heals), and the rare revive kit. Boss kills multiply the odds. All rates
 * live in data/loot.json so balancing never touches code.
 */

import { rollEquipment, weightedPick } from './items.js';

/**
 * Whether an item is allowed to drop in the given map. Items with no `map`
 * field drop everywhere; a `map` (string or array) restricts them to that
 * zone — used for zone-exclusive chase loot.
 * @param {object} def item def
 * @param {string} [mapId] current map id
 * @returns {boolean}
 */
function itemInMap(def, mapId) {
  if (!def.map) return true;
  if (!mapId) return false; // restricted item, but no map context → exclude
  return Array.isArray(def.map) ? def.map.includes(mapId) : def.map === mapId;
}

/**
 * Whether any class in `usableBy` can equip this catalog item definition.
 * Mirrors inventory.canEquip but works on raw defs + a set of class ids.
 * @param {object} def item def from items.json
 * @param {object} classes classes.json
 * @param {string[]} usableBy class ids in the party
 * @returns {boolean}
 */
function itemUsableByAny(def, classes, usableBy) {
  if (Array.isArray(def.classes) && def.classes.length) {
    return usableBy.some((c) => def.classes.includes(c));
  }
  if (def.slot === 'weapon' && def.weapon) {
    return usableBy.some((c) => classes?.[c]?.weapon === def.weapon);
  }
  return true; // universal armor/accessory
}

/**
 * Build a {itemId: dropWeight} map from the catalog (skips non-item keys like
 * _doc). When a party (`usableBy`) is supplied, gear that no member can equip is
 * down-weighted by `offClassFactor` so drops favour usable loot without fully
 * excluding off-class items.
 * @param {object} items items.json
 * @param {object} [classes] classes.json
 * @param {string[]} [usableBy] party class ids
 * @param {number} [offClassFactor=1]
 * @param {string} [mapId] current map id — filters zone-exclusive loot
 * @returns {Record<string, number>}
 */
export function itemDropWeights(items, classes, usableBy, offClassFactor = 1, mapId) {
  const out = {};
  const bias = usableBy && usableBy.length;
  for (const [id, def] of Object.entries(items)) {
    if (!(def && typeof def === 'object' && def.dropWeight > 0)) continue;
    if (!itemInMap(def, mapId)) continue; // zone-exclusive loot drops only in its zone
    let w = def.dropWeight;
    if (bias && !itemUsableByAny(def, classes, usableBy)) w *= offClassFactor;
    out[id] = w;
  }
  return out;
}

/**
 * Roll all drops for a single kill.
 * @param {object} ctx
 * @param {boolean} ctx.isBoss
 * @param {number} ctx.itemLevel typically the hero level
 * @param {number} [ctx.dropMod=1] map drop modifier
 * @param {string[]} [ctx.usableBy] party class ids — biases equipment toward usable gear
 * @param {string} [ctx.mapId] current map id — gates zone-exclusive loot
 * @param {{loot:object, items:object, rarity:object, affixes:object, classes:object}} data
 * @param {import('./rng.js').Rng} rng
 * @returns {{equipment:object|null, food:object|null, reviveKit:boolean}}
 */
export function rollDrops(ctx, data, rng) {
  const L = data.loot;
  const mult = (ctx.isBoss ? L.bossLootMultiplier : 1) * (ctx.dropMod ?? 1);

  let equipment = null;
  if (rng.chance(Math.min(1, L.equipmentDropChance * mult))) {
    // Pick one item from the whole catalog weighted by its own dropWeight; the
    // item carries its intrinsic rarity (rarer items have tiny weights ≈ low %).
    // Gear the party can't equip is down-weighted (offClassDropFactor) so the
    // bag mostly fills with usable loot.
    const weights = itemDropWeights(data.items, data.classes, ctx.usableBy, L.offClassDropFactor ?? 1, ctx.mapId);
    const typeId = weightedPick(weights, rng);
    const rarity = data.items[typeId].rarity ?? 'common';
    equipment = rollEquipment(typeId, rarity, ctx.itemLevel, data, rng);
  }

  let food = null;
  if (rng.chance(Math.min(1, L.foodDropChance * mult))) {
    const foodId = weightedPick(L.foodWeights, rng);
    food = { foodId, ...L.foods[foodId] };
  }

  const reviveKit = rng.chance(Math.min(1, L.reviveKitDropChance * mult));
  const protectionAmulet = rng.chance(Math.min(1, (L.protectionAmuletDropChance ?? 0) * mult));

  return { equipment, food, reviveKit, protectionAmulet };
}

/**
 * Roll a guaranteed equipment drop at (or above) a minimum rarity — used for
 * daily-dungeon clear rewards, which skip the normal drop-chance roll
 * entirely. Still biased toward gear the party can equip.
 * @param {string} minRarityKey e.g. 'rare'
 * @param {number} itemLevel
 * @param {string[]} usableBy party class ids
 * @param {{items:object, classes:object, rarity:object, affixes:object}} data
 * @param {import('./rng.js').Rng} rng
 * @returns {object|null} an equipment instance, or null if no candidate exists
 */
export function rollGuaranteedEquipment(minRarityKey, itemLevel, usableBy, data, rng) {
  const order = Object.keys(data.rarity);
  const minIdx = order.indexOf(minRarityKey);
  if (minIdx < 0) return null;

  const weights = {};
  for (const [id, def] of Object.entries(data.items)) {
    if (!(def && typeof def === 'object' && def.dropWeight > 0)) continue;
    if (order.indexOf(def.rarity) < minIdx) continue; // below the guaranteed floor
    let w = def.dropWeight;
    if (usableBy?.length && !itemUsableByAny(def, data.classes, usableBy)) w *= (data.loot?.offClassDropFactor ?? 1);
    weights[id] = w;
  }
  if (!Object.keys(weights).length) return null;
  const typeId = weightedPick(weights, rng);
  return rollEquipment(typeId, data.items[typeId].rarity, itemLevel, data, rng);
}
